import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { 
  Heart, 
  Users, 
  Home, 
  Clock, 
  Pill, 
  Sparkles,
  ArrowRight,
  Check,
  Shield
} from 'lucide-react';

const services = [
  {
    id: 'personal-care',
    icon: Heart,
    title: 'Personal Care',
    subtitle: 'Dignified Assistance, Every Day',
    description: 'Our compassionate caregivers provide respectful assistance with daily personal care needs while promoting independence and preserving dignity.',
    details: [
      'Bathing and grooming assistance',
      'Dressing and personal hygiene',
      'Mobility assistance and transfers',
      'Incontinence care',
      'Skin care and prevention',
    ],
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop',
  },
  {
    id: 'companionship',
    icon: Users,
    title: 'Companionship',
    subtitle: 'Connection That Enriches Life',
    description: 'Meaningful companionship that combats loneliness and brings joy to each day through genuine human connection and engagement.',
    details: [
      'Friendly conversation and social interaction',
      'Accompaniment to appointments and outings',
      'Hobby and activity engagement',
      'Mental stimulation games and activities',
      'Emotional support and encouragement',
    ],
    image: 'https://images.unsplash.com/photo-1559234938-b60fff04894d?w=600&h=400&fit=crop',
  },
  {
    id: 'daily-living',
    icon: Home,
    title: 'Daily Living Support',
    subtitle: 'Comfort and Order at Home',
    description: 'Assistance with everyday tasks that helps maintain a clean, safe, and comfortable home environment.',
    details: [
      'Meal planning and preparation',
      'Light housekeeping and laundry',
      'Grocery shopping and errands',
      'Organization and home management',
      'Pet care assistance',
    ],
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=400&fit=crop',
  },
  {
    id: 'respite-care',
    icon: Sparkles,
    title: 'Respite Care',
    subtitle: 'Rest for Family Caregivers',
    description: 'Reliable temporary care that gives family caregivers the break they need while ensuring loved ones receive quality attention.',
    details: [
      'Flexible short-term care arrangements',
      'Emergency respite services',
      'Scheduled regular breaks for caregivers',
      'Vacation and event coverage',
      'Consistent quality care standards',
    ],
    image: 'https://images.unsplash.com/photo-1516733968668-dbdce39c4651?w=600&h=400&fit=crop',
  },
  {
    id: 'medication',
    icon: Pill,
    title: 'Medication Reminders',
    subtitle: 'Staying on Track with Health',
    description: 'Gentle reminders and assistance to help maintain medication schedules, supporting overall health and wellness.',
    details: [
      'Scheduled medication reminders',
      'Prescription refill coordination',
      'Organization of medication schedules',
      'Communication with healthcare providers',
      'Health monitoring and reporting',
    ],
    image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=600&h=400&fit=crop',
  },
  {
    id: 'non-medical',
    icon: Clock,
    title: 'Non-Medical In-Home Care',
    subtitle: 'Comprehensive Support Services',
    description: 'A full spectrum of non-medical support services designed to help your loved ones thrive safely at home.',
    details: [
      '24/7 care availability',
      'Customized care plans',
      'Safety monitoring and fall prevention',
      'Transportation assistance',
      'Family communication and updates',
    ],
    image: 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=600&h=400&fit=crop',
  },
];

export const WhatWeDoPage = () => {
  return (
    <>
      <Helmet>
        <title>Our Services | Heritage Home Health LLC</title>
        <meta
          name="description"
          content="Explore the comprehensive in-home care services offered by Heritage Home Health LLC, including personal care, companionship, daily living support, respite care, and more."
        />
      </Helmet>
      <div className="min-h-screen bg-warm-beige" data-testid="what-we-do-page">
        <Navigation />
        <main>
          {/* Hero Section */}
          <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-soft-gold/10 via-transparent to-transparent" />
            <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-soft-gold/20 to-transparent rounded-full blur-3xl" />
            
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                  Our Services
                </span>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy mb-6">
                  What We Do at{' '}
                  <span className="text-gradient-gold">Heritage Home Health LLC</span>
                </h1>
                <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  We provide a comprehensive range of personalized in-home care services 
                  designed to support independence, comfort, and quality of life for your loved ones.
                </p>
              </motion.div>
            </div>
          </section>

          {/* Services List */}
          <section className="py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="space-y-24">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  const isEven = index % 2 === 0;
                  
                  return (
                    <motion.div
                      key={service.id}
                      id={service.id}
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.6 }}
                      className="scroll-mt-32"
                      data-testid={`service-section-${service.id}`}
                    >
                      <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${isEven ? '' : 'lg:flex-row-reverse'}`}>
                        {/* Content */}
                        <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                          <div className="flex items-center space-x-4 mb-6">
                            <div className="w-14 h-14 rounded-2xl bg-soft-gold/20 flex items-center justify-center">
                              <Icon className="w-7 h-7 text-soft-gold" />
                            </div>
                            <div>
                              <h2 className="font-serif text-3xl md:text-4xl text-navy">
                                {service.title}
                              </h2>
                              <p className="text-soft-gold font-medium">{service.subtitle}</p>
                            </div>
                          </div>
                          
                          <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            {service.description}
                          </p>
                          
                          <ul className="space-y-3 mb-8">
                            {service.details.map((detail, i) => (
                              <li key={i} className="flex items-start space-x-3">
                                <div className="w-5 h-5 rounded-full bg-muted-green/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Check className="w-3 h-3 text-muted-green" />
                                </div>
                                <span className="text-gray-700">{detail}</span>
                              </li>
                            ))}
                          </ul>

                          <Link
                            to="/contact"
                            className="group inline-flex items-center space-x-2 text-navy font-semibold hover:text-soft-gold transition-colors"
                          >
                            <span>Inquire About {service.title}</span>
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>

                        {/* Image */}
                        <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                          <div className="relative">
                            <div className="rounded-3xl overflow-hidden shadow-soft-3d-lg">
                              <img
                                src={service.image}
                                alt={service.title}
                                className="w-full h-80 md:h-96 object-cover"
                              />
                            </div>
                            <div className={`absolute ${isEven ? '-bottom-6 -right-6' : '-bottom-6 -left-6'} -z-10 w-full h-full rounded-3xl bg-soft-gold/20`} />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Trust Banner */}
          <section className="py-16 md:py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="glass-strong shadow-soft-3d rounded-3xl p-8 md:p-12"
              >
                <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-2xl bg-soft-gold/20 flex items-center justify-center">
                      <Shield className="w-8 h-8 text-soft-gold" />
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl text-navy">Quality Care You Can Trust</h3>
                      <p className="text-gray-600">Licensed, insured, and background-checked caregivers</p>
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    data-testid="services-page-cta"
                    className="group inline-flex items-center space-x-2 bg-navy text-white px-8 py-4 rounded-full font-semibold hover:bg-deep-blue transition-all shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-1"
                  >
                    <span>Get Started Today</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </section>

          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default WhatWeDoPage;
